/**
 * Named IAM roles: stable `id`s, uppercase `displayName`s, and permission bundles.
 *
 * - **IDs:** `roles/act.{namespace}.{suffix}` (GCP-style resource names).
 * - **Display:** {@link IamRoleDefinition.displayName} uses `DOMAIN.TIER` (e.g. `USER.ADMIN`).
 * - **Enforcement:** runtime checks still use {@link Permission} plus the DB user role column; this file is registry-only.
 */
import { Permission } from '../permissions/permissions.enum';
import type { IamRoleDefinition } from './types';
import { IAM_NAMESPACES } from '../utilities/namespaces.util';

// ---------------------------------------------------------------------------
// Role ID prefix (stable string for all roles exported here)
// ---------------------------------------------------------------------------

/** Prefix for every {@link IamRoleDefinition.id} in this module. */
export const IAM_ROLE_ID_PREFIX = 'roles/act.' as const;

// ---------------------------------------------------------------------------
// Display prefix map (namespace id → DOMAIN segment for displayName)
// ---------------------------------------------------------------------------

/**
 * Maps each {@link IAM_NAMESPACES} `id` to the uppercase domain segment used in
 * {@link IamRoleDefinition.displayName} (e.g. `userAdmin` → `USER` for `USER.ADMIN`).
 * Keep in sync when adding namespaces.
 */
export const IAM_NAMESPACE_DISPLAY_PREFIX: Readonly<
  Record<(typeof IAM_NAMESPACES)[number]['id'], string>
> = {
  userAdmin: 'USER',
  adminPortal: 'ADMIN PORTAL',
  managerOperatingSystem: 'MANAGER OPERATING SYSTEM',
  engineering: 'ENGINEERING',
  memberData: 'MEMBER DATA',
  memberFilters: 'MEMBER FILTERS',
  messaging: 'MESSAGING',
  telephony: 'TELEPHONY',
  clinicalNotesAndVisits: 'CLINICAL',
  hospitalizations: 'HOSPITALIZATIONS',
  appointments: 'APPOINTMENTS',
  needs: 'NEEDS',
  memberFeedback: 'MEMBER FEEDBACK',
  healthSummary: 'HEALTH SUMMARY',
  surveys: 'SURVEYS',
  carePath: 'CARE PATH',
  clients: 'CLIENTS',
  alerts: 'ALERTS',
  changeLogs: 'CHANGE LOGS',
  memberStatus: 'MEMBER STATUS',
  memberTransfers: 'MEMBER TRANSFERS',
  callQuality: 'CALL QUALITY',
  files: 'FILES',
  docket: 'DOCKET',
  guideTargets: 'GUIDE TARGETS',
  reports: 'REPORTS',
};

// ---------------------------------------------------------------------------
// Curated roles (hand-picked bundles for common IAM stories)
// ---------------------------------------------------------------------------

/**
 * Domain-scoped roles (viewer / editor / admin style), not job titles.
 * Keep in sync with `api/libs/server/db/src/migrations/iam-curated-roles.migration-data.ts`.
 */
const curatedIamRoles: readonly IamRoleDefinition[] = [
  {
    id: `${IAM_ROLE_ID_PREFIX}adminPortal.access`,
    displayName: 'ADMIN PORTAL ACCESS',
    description: 'Open the admin portal (no tools / blanket API bypass).',
    permissions: [Permission.CAN_ACCESS_ADMIN_PORTAL],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}managerOperatingSystem.access`,
    displayName: 'MANAGER OPERATING SYSTEM ACCESS',
    description: 'Open the Manager Operating System.',
    permissions: [Permission.CAN_ACCESS_MANAGER_OPERATING_SYSTEM],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}alerts.admin`,
    displayName: 'ALERTS ADMIN',
    description: 'View and administer operational alerts.',
    permissions: [Permission.CAN_ADMINISTER_ALERTS, Permission.CAN_VIEW_ALERTS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}alerts.viewer`,
    displayName: 'ALERTS VIEWER',
    description: 'View operational alerts.',
    permissions: [Permission.CAN_VIEW_ALERTS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}appointments.viewer`,
    displayName: 'APPOINTMENTS VIEWER',
    description: 'See all members’ appointments on the guide docket.',
    permissions: [Permission.CAN_SEE_ALL_APPOINTMENTS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}carePath.configAdmin`,
    displayName: 'CARE PATH CONFIG ADMIN',
    description: 'Configure workflows and see stages.',
    permissions: [
      Permission.CAN_SEE_STAGES,
      Permission.CAN_CONFIGURE_WORKFLOWS,
      Permission.CAN_MANAGE_ASSIGNED_WORKFLOWS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}carePath.participant`,
    displayName: 'CARE PATH PARTICIPANT',
    description:
      'See member stages and manage workflows assigned to the user.',
    permissions: [
      Permission.CAN_SEE_STAGES,
      Permission.CAN_MANAGE_ASSIGNED_WORKFLOWS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}carePath.viewer`,
    displayName: 'CARE PATH VIEWER',
    description: 'See member stages only.',
    permissions: [Permission.CAN_SEE_STAGES],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}changeLogs.admin`,
    displayName: 'CHANGE LOGS ADMIN',
    description: 'View and administer change logs.',
    permissions: [
      Permission.CAN_ADMINISTER_CHANGE_LOGS,
      Permission.CAN_VIEW_CHANGE_LOGS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}changeLogs.viewer`,
    displayName: 'CHANGE LOGS VIEWER',
    description: 'View change logs.',
    permissions: [Permission.CAN_VIEW_CHANGE_LOGS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}clients.admin`,
    displayName: 'CLIENTS ADMIN',
    description:
      'Full client directory capabilities including administration.',
    permissions: [
      Permission.CAN_VIEW_CLIENTS,
      Permission.CAN_MODIFY_CLIENTS,
      Permission.CAN_ADMINISTER_CLIENTS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}clients.editor`,
    displayName: 'CLIENTS EDITOR',
    description: 'View and modify client records.',
    permissions: [Permission.CAN_VIEW_CLIENTS, Permission.CAN_MODIFY_CLIENTS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}clients.viewer`,
    displayName: 'CLIENTS VIEWER',
    description: 'View client directory entries.',
    permissions: [Permission.CAN_VIEW_CLIENTS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}clinicalNotesAndVisits.editor`,
    displayName: 'CLINICAL EDITOR',
    description: 'View and create notes, visits, and schedule events.',
    permissions: [
      Permission.CAN_SEE_NOTES,
      Permission.CAN_CREATE_NOTES,
      Permission.CAN_SEE_VISITS,
      Permission.CAN_CREATE_VISITS,
      Permission.CAN_SCHEDULE_EVENTS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}clinicalNotesAndVisits.viewer`,
    displayName: 'CLINICAL VIEWER',
    description: 'View clinical notes and visits.',
    permissions: [Permission.CAN_SEE_NOTES, Permission.CAN_SEE_VISITS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}files.viewer`,
    displayName: 'FILES VIEWER',
    description: 'See files across the experience.',
    permissions: [Permission.CAN_SEE_ALL_FILES],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}docket.embed`,
    displayName: 'DOCKET EMBED',
    description: 'Use docket embed experiences.',
    permissions: [Permission.CAN_USE_DOCKET_EMBED_FEATURE],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}guideTargets.editor`,
    displayName: 'GUIDE TARGETS EDITOR',
    description: 'View and update guide targets.',
    permissions: [
      Permission.CAN_VIEW_GUIDE_TARGETS,
      Permission.CAN_UPDATE_GUIDE_TARGETS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}guideTargets.viewer`,
    displayName: 'GUIDE TARGETS VIEWER',
    description: 'View guide targets.',
    permissions: [Permission.CAN_VIEW_GUIDE_TARGETS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}reports.editor`,
    displayName: 'REPORTS EDITOR',
    description:
      'View and edit customer reports and templates on reports.reemahealth.com.',
    permissions: [Permission.CAN_VIEW_REPORTS, Permission.CAN_EDIT_REPORTS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}healthSummary.editor`,
    displayName: 'HEALTH SUMMARY EDITOR',
    description:
      'Manage health summary responses and provide summary feedback.',
    permissions: [
      Permission.CAN_MANAGE_HEALTH_SUMMARY_RESPONSES,
      Permission.CAN_PROVIDE_MEMBER_SUMMARY_FEEDBACK,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}hospitalizations.admin`,
    displayName: 'HOSPITALIZATIONS ADMIN',
    description:
      'Full hospitalization visibility including all-members views and CCD export.',
    permissions: [
      Permission.CAN_SEE_HOSPITALIZATIONS,
      Permission.CAN_UPDATE_HOSPITALIZATIONS,
      Permission.CAN_SEE_ALL_HOSPITALIZATIONS,
      Permission.CAN_EXPORT_CCD,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}hospitalizations.editor`,
    displayName: 'HOSPITALIZATIONS EDITOR',
    description: 'See and update hospitalizations for accessible members.',
    permissions: [
      Permission.CAN_SEE_HOSPITALIZATIONS,
      Permission.CAN_UPDATE_HOSPITALIZATIONS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}hospitalizations.viewer`,
    displayName: 'HOSPITALIZATIONS VIEWER',
    description: 'See hospitalizations for accessible members.',
    permissions: [Permission.CAN_SEE_HOSPITALIZATIONS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}memberData.admin`,
    displayName: 'MEMBER DATA ADMIN',
    description:
      'Full member directory access including cross-member and view-all affordances.',
    permissions: [
      Permission.CAN_VIEW_MEMBERS,
      Permission.CAN_SEE_ALL_USER_DATA,
      Permission.CAN_UPDATE_ALL_USER_DATA,
      Permission.CAN_ACCESS_ALL_MEMBERS,
      Permission.CAN_USE_VIEW_ALL_FEATURE,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}memberData.editor`,
    displayName: 'MEMBER DATA EDITOR',
    description: 'View and update member profile data.',
    permissions: [
      Permission.CAN_VIEW_MEMBERS,
      Permission.CAN_SEE_ALL_USER_DATA,
      Permission.CAN_UPDATE_ALL_USER_DATA,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}memberData.viewer`,
    displayName: 'MEMBER DATA VIEWER',
    description: 'View member directory and profile data.',
    permissions: [Permission.CAN_VIEW_MEMBERS, Permission.CAN_SEE_ALL_USER_DATA],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}memberFeedback.admin`,
    displayName: 'MEMBER FEEDBACK ADMIN',
    description: 'Comments plus operational member feedback management.',
    permissions: [Permission.CAN_COMMENT, Permission.CAN_MANAGE_MEMBER_FEEDBACK],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}memberFeedback.participant`,
    displayName: 'MEMBER FEEDBACK PARTICIPANT',
    description: 'May comment on member-facing threads.',
    permissions: [Permission.CAN_COMMENT],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}memberFilters.operator`,
    displayName: 'MEMBER FILTERS OPERATOR',
    description: 'Use member list filters (client, guide, segment, status).',
    permissions: [
      Permission.CAN_FILTER_CLIENT,
      Permission.CAN_FILTER_GUIDE,
      Permission.CAN_FILTER_SEGMENT,
      Permission.CAN_FILTER_STATUS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}messaging.admin`,
    displayName: 'MESSAGING ADMIN',
    description:
      'Full messaging capabilities including SMS visibility and templates.',
    permissions: [
      Permission.CAN_ASSIGN_PATIENT_MESSAGE,
      Permission.CAN_SEND_PATIENT_MESSAGE,
      Permission.CAN_SEE_SMS_MESSAGES,
      Permission.CAN_USE_MESSAGE_TEMPLATE_FEATURE,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}messaging.editor`,
    displayName: 'MESSAGING EDITOR',
    description: 'View SMS, assign, send, and use templates.',
    permissions: [
      Permission.CAN_SEE_SMS_MESSAGES,
      Permission.CAN_ASSIGN_PATIENT_MESSAGE,
      Permission.CAN_SEND_PATIENT_MESSAGE,
      Permission.CAN_USE_MESSAGE_TEMPLATE_FEATURE,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}messaging.viewer`,
    displayName: 'MESSAGING VIEWER',
    description: 'View SMS threads.',
    permissions: [Permission.CAN_SEE_SMS_MESSAGES],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}needs.admin`,
    displayName: 'NEEDS ADMIN',
    description: 'Full needs capabilities including deleting need actions.',
    permissions: [
      Permission.CAN_SEE_USER_NEEDS,
      Permission.CAN_UPDATE_USER_NEEDS,
      Permission.CAN_DELETE_NEED_ACTIONS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}needs.editor`,
    displayName: 'NEEDS EDITOR',
    description:
      'View and update member needs without destructive actions.',
    permissions: [Permission.CAN_SEE_USER_NEEDS, Permission.CAN_UPDATE_USER_NEEDS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}needs.viewer`,
    displayName: 'NEEDS VIEWER',
    description: 'View member needs.',
    permissions: [Permission.CAN_SEE_USER_NEEDS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}surveys.admin`,
    displayName: 'SURVEYS ADMIN',
    description: 'Full survey administration including reopening.',
    permissions: [
      Permission.CAN_ADMINISTRATE_SURVEYS,
      Permission.CAN_COMPLETE_SURVEYS,
      Permission.CAN_VIEW_SURVEYS,
      Permission.CAN_REOPEN_SURVEYS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}surveys.viewer`,
    displayName: 'SURVEYS VIEWER',
    description: 'View and complete assigned surveys.',
    permissions: [Permission.CAN_VIEW_SURVEYS, Permission.CAN_COMPLETE_SURVEYS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}telephony.admin`,
    displayName: 'TELEPHONY ADMIN',
    description:
      'Full telephony namespace (calls, recordings, reassignment, transcripts).',
    permissions: [
      Permission.CAN_CALL_MEMBERS,
      Permission.CAN_SEE_ALL_CALLS,
      Permission.CAN_CHANGE_PHONE_CALL_SUBJECT,
      Permission.CAN_SEE_ALL_VOICEMAILS,
      Permission.CAN_LISTEN_TO_CALL_RECORDINGS,
      Permission.CAN_REASSIGN_CALLS_AND_TEXTS,
      Permission.CAN_RERUN_CALL_TRANSCRIPTIONS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}telephony.editor`,
    displayName: 'TELEPHONY EDITOR',
    description:
      'Listen and review calls plus place calls, manage subjects, reassign, and rerun transcripts.',
    permissions: [
      Permission.CAN_SEE_ALL_CALLS,
      Permission.CAN_SEE_ALL_VOICEMAILS,
      Permission.CAN_LISTEN_TO_CALL_RECORDINGS,
      Permission.CAN_CALL_MEMBERS,
      Permission.CAN_CHANGE_PHONE_CALL_SUBJECT,
      Permission.CAN_REASSIGN_CALLS_AND_TEXTS,
      Permission.CAN_RERUN_CALL_TRANSCRIPTIONS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}telephony.viewer`,
    displayName: 'TELEPHONY VIEWER',
    description: 'Listen to calls, voicemails, and recordings.',
    permissions: [
      Permission.CAN_SEE_ALL_CALLS,
      Permission.CAN_SEE_ALL_VOICEMAILS,
      Permission.CAN_LISTEN_TO_CALL_RECORDINGS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}callQuality.supervisor`,
    displayName: 'CALL QUALITY SUPERVISOR',
    description:
      'Assign call reviews and override under-two-minute engagement policy.',
    permissions: [
      Permission.CAN_ASSIGN_CALL_REVIEWS,
      Permission.CAN_OVERRIDE_UNDER_TWO_MINUTE_ENGAGEMENT,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}engineering.scriptsAndTests`,
    displayName: 'ENGINEERING SCRIPTS AND TESTS',
    description: 'Run scripts and tests (legacy SUPER_ADMIN slice).',
    permissions: [Permission.CAN_RUN_SCRIPTS, Permission.CAN_RUN_TESTS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}engineering.seedDataGenerator`,
    displayName: 'SEED DATA GENERATOR',
    description:
      'Generate seed data without other engineering namespace permissions.',
    permissions: [Permission.CAN_GENERATE_SEED_DATA],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}engineering.slackOperator`,
    displayName: 'SLACK OPERATOR',
    description: 'Administer internal Slack (legacy care-team slice).',
    permissions: [Permission.CAN_ADMIN_SLACK],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}hospitalizations.allMembersViewer`,
    displayName: 'HOSPITALIZATIONS ALL-MEMBERS VIEWER',
    description: 'See all members’ hospitalizations (legacy ADMIN slice).',
    permissions: [Permission.CAN_SEE_ALL_HOSPITALIZATIONS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}memberData.viewAllOperator`,
    displayName: 'MEMBER DATA VIEW-ALL',
    description:
      'Use view-all affordance without cross-member access-all (legacy lead slice).',
    permissions: [Permission.CAN_USE_VIEW_ALL_FEATURE],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}memberStatus.requestCreator`,
    displayName: 'MEMBER STATUS REQUEST CREATOR',
    description:
      'Create member status change requests only (legacy care-team slice).',
    permissions: [Permission.CAN_CREATE_MEMBER_STATUS_CHANGE_REQUESTS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}memberTransfers.auditorAndCreator`,
    displayName: 'MEMBER TRANSFERS AUDITOR AND CREATOR',
    description:
      'Audit and create transfer requests (legacy ADMIN slice; excludes without-guide variants).',
    permissions: [
      Permission.CAN_AUDIT_MEMBER_TRANSFER_REQUESTS,
      Permission.CAN_CREATE_MEMBER_TRANSFER_REQUESTS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}memberTransfers.requestCreator`,
    displayName: 'MEMBER TRANSFER REQUEST CREATOR',
    description: 'Create member transfer requests only (legacy care-team slice).',
    permissions: [Permission.CAN_CREATE_MEMBER_TRANSFER_REQUESTS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}messaging.outboundOperator`,
    displayName: 'MESSAGING OUTBOUND OPERATOR',
    description:
      'Assign, send, and templates without SMS inbox visibility (legacy care-team slice).',
    permissions: [
      Permission.CAN_ASSIGN_PATIENT_MESSAGE,
      Permission.CAN_SEND_PATIENT_MESSAGE,
      Permission.CAN_USE_MESSAGE_TEMPLATE_FEATURE,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}needs.deleteNeedActions`,
    displayName: 'NEEDS DELETE ACTIONS',
    description: 'Delete need actions only (legacy SUPER_ADMIN slice).',
    permissions: [Permission.CAN_DELETE_NEED_ACTIONS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}surveys.administration`,
    displayName: 'SURVEYS ADMINISTRATION',
    description:
      'Administer and reopen surveys without duplicating viewer/complete (legacy ADMIN delta).',
    permissions: [
      Permission.CAN_ADMINISTRATE_SURVEYS,
      Permission.CAN_REOPEN_SURVEYS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}telephony.careTeamCore`,
    displayName: 'TELEPHONY CARE TEAM CORE',
    description:
      'Calls, subject changes, and all voicemails without see-all-calls or recordings (legacy care-team slice).',
    permissions: [
      Permission.CAN_CALL_MEMBERS,
      Permission.CAN_CHANGE_PHONE_CALL_SUBJECT,
      Permission.CAN_SEE_ALL_VOICEMAILS,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}telephony.listenToCallRecordings`,
    displayName: 'CALL RECORDINGS LISTENER',
    description: 'Listen to call recordings only (legacy ADMIN slice).',
    permissions: [Permission.CAN_LISTEN_TO_CALL_RECORDINGS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}telephony.rerunCallTranscriptions`,
    displayName: 'RERUN CALL TRANSCRIPTIONS',
    description: 'Rerun call transcriptions only (legacy SUPER_ADMIN slice).',
    permissions: [Permission.CAN_RERUN_CALL_TRANSCRIPTIONS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}telephony.reassignCallsAndTexts`,
    displayName: 'REASSIGN CALLS AND TEXTS',
    description: 'Reassign calls and texts only (legacy ADMIN slice).',
    permissions: [Permission.CAN_REASSIGN_CALLS_AND_TEXTS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}telephony.seeAllCallsViewer`,
    displayName: 'SEE ALL CALLS',
    description: 'See all calls only (legacy ADMIN slice).',
    permissions: [Permission.CAN_SEE_ALL_CALLS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}userAdmin.accountCreator`,
    displayName: 'USER ACCOUNT CREATOR',
    description: 'Create internal users only (legacy ADMIN; no impersonation).',
    permissions: [Permission.CAN_CREATE_USERS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}userAdmin.appAccess.admin`,
    displayName: 'USER APP ACCESS ADMIN',
    description:
      'May administer member app access flags for internal users.',
    permissions: [Permission.CAN_ADMINISTER_APP_ACCESS],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}userAdmin.adminRoles.admin`,
    displayName: 'USER ADMIN ROLES ADMIN',
    description:
      'Assign or change elevated staff roles (Admin, Care Team Director, Care Team Assistant Director) in user administration.',
    permissions: [Permission.CAN_ADMINISTER_ADMIN_ROLES],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}userAdmin.enrollment.editor`,
    displayName: 'USER ENROLLMENT EDITOR',
    description: 'Enroll members and orchestrate training workflows.',
    permissions: [
      Permission.CAN_ENROLL_MEMBERS,
      Permission.CAN_ORCHESTRATE_TRAINING,
    ],
  },
  {
    id: `${IAM_ROLE_ID_PREFIX}userAdmin.provisioning.admin`,
    displayName: 'USER PROVISIONING ADMIN',
    description: 'Create internal users and impersonate for support.',
    permissions: [
      Permission.CAN_CREATE_USERS,
      Permission.CAN_IMPERSONATE_USERS,
    ],
  },
];

// ---------------------------------------------------------------------------
// Namespace-admin roles (one full-capability role per namespace)
// ---------------------------------------------------------------------------

/**
 * For each entry in {@link IAM_NAMESPACES}, a synthetic role that grants **every** permission
 * in that namespace. Useful for spreadsheets, parity checks, and future “give me everything
 * in topic X” bindings.
 *
 * - **id:** `roles/act.{namespaceId}.namespaceAdmin`
 * - **displayName:** `{IAM_NAMESPACE_DISPLAY_PREFIX[namespaceId]} ADMIN` (e.g. `MEMBER DATA ADMIN`)
 */
const namespaceAdminRoles: readonly IamRoleDefinition[] = IAM_NAMESPACES.map(ns => ({
  id: `${IAM_ROLE_ID_PREFIX}${ns.id}.namespaceAdmin`,
  displayName: `${IAM_NAMESPACE_DISPLAY_PREFIX[ns.id]} ADMIN`,
  description: `All permissions in the ${ns.title} namespace (${ns.id}).`,
  permissions: ns.permissions,
}));

// ---------------------------------------------------------------------------
// Combined export
// ---------------------------------------------------------------------------

/** All curated roles followed by every namespace-admin role (see sections above). */
export const IAM_ROLES: readonly IamRoleDefinition[] = [
  ...curatedIamRoles,
  ...namespaceAdminRoles,
];
